import { z } from 'zod';
import { PartType } from '../../constants/part-type.js';
import { isStockablePartType } from '../../utils/is-stockable-part-type.js';
import { nonNegativeInt, nonNegativeNumber, optionalUuidString, requiredUuidString, } from '../zod-helpers/index.js';
import { MAX_PART_BRAND_IDS, MAX_VENDOR_PARTS_PER_CREATE, MAX_VENDOR_PARTS_PER_CREATE_MESSAGE, PART_CONSTRAINTS, } from './constraints.js';
import { checkPrimaryVendorCount, checkReorderPointVsOrderUpTo } from './rules.js';
export const partCreateVendorPartInputSchema = z.object({
    vendorId: requiredUuidString('Vendor is required.', 'Invalid vendor selection.'),
    vendorPartNumber: z
        .string()
        .trim()
        .min(1, 'Vendor part number is required for vendor associations.')
        .max(PART_CONSTRAINTS.vendorPartNumber.maxLength),
    lastCost: nonNegativeNumber(PART_CONSTRAINTS.vendorLastCost.label).optional().nullable(),
    leadTime: nonNegativeInt(PART_CONSTRAINTS.vendorLeadTime.label).optional().nullable(),
    qtyMin: nonNegativeNumber(PART_CONSTRAINTS.vendorQtyMin.label).optional().nullable(),
    defaultFlag: z.boolean().optional().nullable(),
});
export const createPartInputSchema = z
    .object({
    num: z
        .string()
        .trim()
        .min(1, `${PART_CONSTRAINTS.num.label} is required.`)
        .max(PART_CONSTRAINTS.num.maxLength),
    name: z.string().trim().max(PART_CONSTRAINTS.name.maxLength).optional().nullable(),
    description: z
        .string()
        .trim()
        .max(PART_CONSTRAINTS.description.maxLength, `${PART_CONSTRAINTS.description.label} is too long.`)
        .optional()
        .nullable(),
    revision: z.string().max(PART_CONSTRAINTS.revision.maxLength).optional().nullable(),
    typeId: z.enum(PartType, 'Part type must be a valid selection.'),
    uomId: requiredUuidString('Stocking UOM is required.', 'Invalid stocking UOM selection.'),
    trackingFlag: z.boolean().optional().nullable(),
    // Blocks creation since the receiving workflow can't receive serialized parts yet — lift this refine when serial receiving lands.
    serializedFlag: z
        .boolean()
        .optional()
        .nullable()
        .refine((value) => value !== true, {
        message: 'Serialized parts cannot be received yet, so they cannot be created.',
    }),
    controlledFlag: z.boolean().optional().nullable(),
    stdCost: nonNegativeNumber(PART_CONSTRAINTS.stdCost.label).optional().nullable(),
    leadTime: nonNegativeInt(PART_CONSTRAINTS.leadTime.label).optional().nullable(),
    activeFlag: z.boolean().optional().nullable(),
    inventoryAccountId: optionalUuidString('Invalid inventory account ID.').optional().nullable(),
    cogsAccountId: optionalUuidString('Invalid COGS account ID.').optional().nullable(),
    varianceAccountId: optionalUuidString('Invalid variance account ID.').optional().nullable(),
    scrapAccountId: optionalUuidString('Invalid scrap account ID.').optional().nullable(),
    defaultProductId: optionalUuidString('Invalid product selection.').optional().nullable(),
    brandIds: z
        .array(requiredUuidString('Brand is required.', 'Invalid brand selection.'))
        .min(1, 'Select at least one brand.')
        .max(MAX_PART_BRAND_IDS, `Select at most ${MAX_PART_BRAND_IDS} brands.`)
        .transform((ids) => [...new Set(ids)]),
    defaultLocation: z
        .object({
        locationGroupId: requiredUuidString('Warehouse is required.', 'Invalid warehouse selection.'),
        locationId: requiredUuidString('Location is required.', 'Invalid location selection.'),
    })
        .optional()
        .nullable(),
    partReorder: z
        .object({
        locationGroupId: optionalUuidString('Invalid warehouse selection.').optional().nullable(),
        reorderPoint: nonNegativeNumber(PART_CONSTRAINTS.reorderPoint.label).optional().nullable(),
        orderUpToLevel: nonNegativeNumber(PART_CONSTRAINTS.orderUpToLevel.label)
            .optional()
            .nullable(),
    })
        .optional()
        .nullable(),
    vendorParts: z
        .array(partCreateVendorPartInputSchema)
        .max(MAX_VENDOR_PARTS_PER_CREATE, MAX_VENDOR_PARTS_PER_CREATE_MESSAGE)
        .optional()
        .nullable(),
})
    .superRefine((input, context) => {
    const reorderMessage = checkReorderPointVsOrderUpTo(input.partReorder?.reorderPoint, input.partReorder?.orderUpToLevel);
    if (reorderMessage) {
        context.addIssue({
            code: 'custom',
            message: reorderMessage,
            path: ['partReorder', 'reorderPoint'],
        });
    }
    const primaryVendorMessage = checkPrimaryVendorCount(input.vendorParts ?? []);
    if (primaryVendorMessage) {
        context.addIssue({ code: 'custom', message: primaryVendorMessage, path: ['vendorParts'] });
    }
    if (isStockablePartType(input.typeId))
        return;
    if (input.defaultLocation) {
        context.addIssue({
            code: 'custom',
            path: ['defaultLocation'],
            message: 'This part type is not stocked, so it cannot have a default location.',
        });
    }
    if (input.partReorder) {
        context.addIssue({
            code: 'custom',
            path: ['partReorder'],
            message: 'This part type is not stocked, so it cannot have reorder levels.',
        });
    }
    if (input.trackingFlag === true) {
        context.addIssue({
            code: 'custom',
            path: ['trackingFlag'],
            message: 'This part type is not stocked, so it cannot be lot tracked.',
        });
    }
});
//# sourceMappingURL=create-input.js.map